import { useCallback, useEffect, useRef, useState } from 'react';
import { PageIntro } from '@/components/help/PageIntro';
import { LabelWithHint } from '@/components/help/LabelWithHint';
import { Button } from '@/components/ui/button';
import { socialApi, type MediaAsset } from '@/lib/social-api';
import { toast } from 'sonner';
import { Loader2, Trash2, Upload } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MediaLibraryPageProps {
  onNavigate: (path: string) => void;
}

const ALLOWED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'video/mp4', 'video/quicktime'];

export function MediaLibraryPage({ onNavigate }: MediaLibraryPageProps) {
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const uploadingRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setAssets(await socialApi.listMedia());
    } catch {
      toast.error('Medien konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const uploadFiles = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (!list.length || uploadingRef.current) return;
    uploadingRef.current = true;
    setUploading(true);
    try {
      const uploaded: MediaAsset[] = [];
      for (const file of list) {
        if (!ALLOWED.includes(file.type)) {
          toast.error(`${file.name}: Dateityp nicht erlaubt.`);
          continue;
        }
        if (file.type.startsWith('image/') && file.size > 8 * 1024 * 1024) {
          toast.error(`${file.name}: Bilder max. 8 MB.`);
          continue;
        }
        try {
          uploaded.push(await socialApi.uploadMedia(file));
        } catch (e) {
          toast.error(`${file.name}: ${e instanceof Error ? e.message : 'Upload fehlgeschlagen.'}`);
        }
      }
      if (uploaded.length) {
        setAssets((previous) => [...uploaded, ...previous]);
        toast.success(`${uploaded.length} Medium/Medien hochgeladen.`);
      }
    } finally {
      uploadingRef.current = false;
      setUploading(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await socialApi.deleteMedia(id);
      toast.success('Medium gelöscht.');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Löschen fehlgeschlagen.');
    }
  };

  return (
    <div className="w-full">
      <PageIntro
        title="Medienbibliothek"
        description="Lade Bilder und Videos hoch, die der Composer und Meta/YouTube zum Veröffentlichen nutzen. Öffentliche URLs sind für Instagram Pflicht."
        className="max-w-none"
        actions={
          <Button onClick={() => onNavigate('/composer')}>Im Composer verwenden</Button>
        }
      />

      <LabelWithHint
        label="Upload"
        hint="Erlaubt: JPEG, PNG, WebP, GIF, MP4, MOV. Stories/Reels/Shorts brauchen 9:16 für beste Ergebnisse."
      />

      <div
        className={cn(
          'mt-3 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-12 transition-colors',
          dragOver
            ? 'border-border bg-card-hover/50 dark:bg-primary-hover/20'
            : 'border-border'
        )}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          void uploadFiles(e.dataTransfer.files);
        }}
      >
        {uploading ? (
          <Loader2 className="h-8 w-8 animate-spin text-foreground" />
        ) : (
          <>
            <Upload className="mb-3 h-8 w-8 text-muted" />
            <p className="text-sm text-muted">Dateien hierher ziehen oder</p>
            <label className="mt-3 cursor-pointer">
              <span className="inline-flex items-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover">
                Dateien wählen
              </span>
              <input
                type="file"
                aria-label="Medien hochladen"
                className="hidden"
                multiple
                accept={ALLOWED.join(',')}
                onChange={(e) => {
                  if (e.target.files) void uploadFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
          </>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-foreground" />
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {assets.length === 0 && (
            <p className="col-span-full text-sm text-muted">Noch keine Medien hochgeladen.</p>
          )}
          {assets.map((asset) => (
            <div
              key={asset.id}
              className="group relative overflow-hidden rounded-xl border border-border bg-background dark:bg-card"
            >
              {asset.mime_type.startsWith('video/') ? (
                <video src={asset.public_url || undefined} className="aspect-square w-full object-cover" muted />
              ) : (
                <img
                  src={asset.public_url || undefined}
                  alt=""
                  className="aspect-square w-full object-cover"
                />
              )}
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent p-2 transition-opacity md:opacity-0 md:group-hover:opacity-100 group-focus-within:opacity-100">
                <span className="truncate text-[10px] text-white">{asset.mime_type}</span>
                <button
                  type="button"
                  className="rounded p-1 text-white hover:bg-card/20"
                  onClick={() => void remove(asset.id)}
                  aria-label="Löschen"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
